import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock3,
  Download,
  Hourglass,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  allocationValidationApi,
  type AllocationValidationRule,
  type AllocationValidationRunDetail,
  type AllocationValidationRunDetailRule,
  type AllocationValidationRun,
  type AllocationValidationRunStatus,
  type AllocationValidationRunStatusFilter,
  type AllocationSapConnectionStatus,
} from '../../services/allocationValidationApi';
import { sapOAuthService } from '../../services/sapOAuthService';
import {
  MultiSelectDropdown,
  type MultiSelectTableColumn,
} from '../UI/MultiSelectDropdown';
import { SidePanel } from '../UI/SidePanel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../UI/tooltip';
import { useGlobalToast } from '../UI/toaster';

type AllocationRunStatus = AllocationValidationRunStatus;
type AllocationRun = AllocationValidationRun;
type AllocationRule = AllocationValidationRule;
type AllocationRuleKpiFields = Pick<AllocationRule, 'allocStep' | 'ratioKpi' | 'allocKpi'>;
type RuleResultStatusFilter = 'all' | 'completed' | 'failed' | 'in_progress';
type RuleValidationResultFilter = 'all' | 'all_good' | 'needs_review' | 'none';
type WatchedAllocationRun = {
  name: string;
  period: string;
  completionNotified: boolean;
};

type AllocationKpi = {
  id: string;
  label: string;
  value: string;
  helperText: string;
  trendText?: string;
  trendDirection?: 'up' | 'down';
  visual: 'line' | 'bars';
};

const allocationKpis: AllocationKpi[] = [
  {
    id: 'total-runs',
    label: 'Total Runs',
    value: '128',
    helperText: 'All time',
    visual: 'line',
  },
  {
    id: 'success-rate',
    label: 'Success Rate',
    value: '92.4%',
    helperText: 'vs last 30 days',
    trendText: '8.7%',
    trendDirection: 'up',
    visual: 'bars',
  },
  {
    id: 'average-duration',
    label: 'Avg. Duration',
    value: '18m 32s',
    helperText: 'vs last 30 days',
    trendText: '2.4m',
    trendDirection: 'down',
    visual: 'line',
  },
  {
    id: 'failed-runs',
    label: 'Failed Runs',
    value: '10',
    helperText: 'vs last 30 days',
    trendText: '12.5%',
    trendDirection: 'up',
    visual: 'bars',
  },
  {
    id: 'in-progress-runs',
    label: 'In Progress Runs',
    value: '24',
    helperText: 'currently active',
    visual: 'line',
  },
];

const kpiIcons: Record<string, LucideIcon> = {
  'total-runs': CalendarDays,
  'success-rate': CheckCircle2,
  'average-duration': Clock3,
  'failed-runs': XCircle,
  'in-progress-runs': Hourglass,
};

const kpiBarHeights: Record<string, number[]> = {
  'success-rate': [38, 58, 50, 68, 82],
  'failed-runs': [70, 48, 36, 58, 26],
};

const statusStyles: Record<AllocationRunStatus, string> = {
  Completed: 'bg-success-100 text-success-700',
  'In Progress': 'bg-warning-50 text-warning-700',
};

const isRunDetailsEligible = (status: AllocationRunStatus) => (
  status === 'Completed' || status === 'In Progress'
);

const isRunPollingTerminal = (status: AllocationRunStatus) => status === 'Completed';

const formatRunDate = (value: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const dateText = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  const timeText = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  return `${dateText} ${timeText}`;
};

const AllocationSparkline: React.FC<{ variant: 'line' | 'bars'; kpiId: string }> = ({ variant, kpiId }) => {
  if (variant === 'line') {
    return (
      <svg className="h-12 w-24 text-primary/60" viewBox="0 0 96 48" fill="none" aria-hidden="true">
        <path
          d="M6 42 C18 28 26 28 36 35 C44 40 52 34 60 36 C70 39 75 31 82 20 C86 14 90 12 94 12"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M6 42 C18 28 26 28 36 35 C44 40 52 34 60 36 C70 39 75 31 82 20 C86 14 90 12 94 12 L94 48 L6 48 Z"
          fill="currentColor"
          opacity="0.16"
        />
      </svg>
    );
  }

  return (
    <div className="flex h-14 items-end gap-1.5" aria-hidden="true">
      {kpiBarHeights[kpiId].map((height, index) => (
        <span
          key={`${kpiId}-bar-${index}`}
          className="w-1.5 rounded-t-sm bg-primary/20"
          style={{ height: `${Math.round(height * 0.62)}px` }}
        />
      ))}
    </div>
  );
};

const PaginationButton: React.FC<{
  children: React.ReactNode;
  active?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: () => void;
}> = ({ children, active = false, ariaLabel, disabled = false, onClick }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    className={`flex h-7 min-w-7 items-center justify-center rounded-md border text-xs font-semibold shadow-sm transition-colors ${
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-foreground'
    } ${disabled ? 'cursor-not-allowed opacity-45' : 'hover:border-primary/40 hover:text-primary'}`}
  >
    {children}
  </button>
);

const ActionTooltip: React.FC<{
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
}> = ({ content, children, side = 'top' }) => (
  <TooltipProvider delayDuration={180}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent side={side} align="center" className="z-[1300] text-xs">
        {content}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const PAGE_SIZE_OPTIONS = [15, 25, 50];
const RULES_PAGE_SIZE = 20;
const RUNS_POLLING_INTERVAL_MS = 90_000;
const SHOW_ALLOCATION_KPIS = false;
const RUN_STATUS_FILTER_OPTIONS: Array<{ value: AllocationValidationRunStatusFilter; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
];

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: string; message?: string; error?: string } | string } }).response;
    if (typeof response?.data === 'string' && response.data.trim()) return response.data;
    if (response?.data && typeof response.data === 'object') {
      return response.data.detail || response.data.message || response.data.error || fallback;
    }
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

const displayRuleValue = (value: string) => value || '-';
const isRatioAllocationStep = (value: string) => value.trim().toLowerCase() === 'ratio';
const getRuleSourceDisplay = (rule: Pick<AllocationRule, 'source' | 'allocStep'>) => {
  const source = rule.source.trim();
  if (source) return source;
  return isRatioAllocationStep(rule.allocStep) ? 'Shared' : '-';
};
const getRuleKpiDisplay = (rule: AllocationRuleKpiFields) => (
  isRatioAllocationStep(rule.allocStep) ? displayRuleValue(rule.ratioKpi) : displayRuleValue(rule.allocKpi)
);
const getRuleKpiLabel = (rule: AllocationRuleKpiFields) => (
  isRatioAllocationStep(rule.allocStep) ? 'Ratio KPI' : 'Allocation KPI'
);
const formatAllocationRuleOption = (rule: AllocationRule) => (
  [
    rule.rule,
    getRuleSourceDisplay(rule),
    rule.allocStep,
    rule.ratioKpi,
    rule.allocKpi,
    getRuleKpiDisplay(rule),
  ].filter(Boolean).join(' ')
);
const renderAllocationRuleOption = (rule: AllocationRule) => {
  const ruleName = displayRuleValue(rule.rule);
  const kpiDisplay = getRuleKpiDisplay(rule);
  const kpiLabel = getRuleKpiLabel(rule);
  const sourceDisplay = getRuleSourceDisplay(rule);

  return (
    <span className="flex min-w-0 flex-1 items-start justify-between gap-3">
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-foreground" title={`${kpiLabel}: ${kpiDisplay}`}>
          {kpiDisplay}
        </span>
        <span className="mt-0.5 block truncate text-[0.7rem] text-muted-foreground" title={ruleName}>
          Rule: {ruleName}
        </span>
      </span>
      <span className="mt-0.5 flex flex-shrink-0 items-center gap-1.5">
        <span className="inline-flex max-w-[4.5rem] items-center rounded-md bg-primary/10 px-2 py-0.5 text-[0.7rem] font-semibold text-primary">
          <span className="truncate">{sourceDisplay}</span>
        </span>
        <span className="inline-flex max-w-[8rem] items-center rounded-md bg-muted px-2 py-0.5 text-[0.7rem] font-medium text-muted-foreground">
          <span className="truncate">{displayRuleValue(rule.allocStep)}</span>
        </span>
      </span>
    </span>
  );
};
const allocationRuleSelectionColumns: Array<MultiSelectTableColumn<AllocationRule>> = [
  { id: 'rule', title: 'Rule', accessor: (rule) => displayRuleValue(rule.rule), className: 'min-w-[14rem]' },
  { id: 'source', title: 'Source', accessor: (rule) => getRuleSourceDisplay(rule), className: 'w-[5.5rem] min-w-[5.5rem]' },
  { id: 'kpi', title: 'KPI', accessor: (rule) => getRuleKpiDisplay(rule), className: 'min-w-[11rem]' },
  { id: 'allocStep', title: 'Allocation Step', accessor: (rule) => displayRuleValue(rule.allocStep), className: 'min-w-[10rem]' },
  { id: 'allocKpi', title: 'Allocation KPI', accessor: (rule) => displayRuleValue(rule.allocKpi), className: 'min-w-[12rem]' },
  { id: 'ratioKpi', title: 'Ratio KPI', accessor: (rule) => displayRuleValue(rule.ratioKpi), className: 'min-w-[12rem]' },
];
const formatRuleColumnHeader = (key: string) => (
  key
    .split('_')
    .map((part) => (part.toLowerCase() === 'kpi' ? 'KPI' : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ')
);
const parseCount = (value: string) => {
  const parsed = Number(String(value || '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};
const normalizeRuleStatus = (value: string): RuleResultStatusFilter => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'failed' || normalized === 'failure' || normalized === 'error') return 'failed';
  if (normalized === 'success' || normalized === 'completed' || normalized === 'succeeded') return 'completed';
  return 'in_progress';
};
const formatRuleStatus = (value: string) => {
  if (!value) return '-';
  if (normalizeRuleStatus(value) === 'completed') return 'Completed';
  if (normalizeRuleStatus(value) === 'failed') return 'Failed';
  if (normalizeRuleStatus(value) === 'in_progress') return 'In Progress';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};
const getRuleStatusClass = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'failed') return 'bg-error-50 text-error-700';
  if (normalized === 'success' || normalized === 'completed') return 'bg-success-100 text-success-700';
  return 'bg-warning-50 text-warning-700';
};
const isFailedRuleStatus = (value: string) => value.trim().toLowerCase() === 'failed';
const getRuleValidationResult = (rule: AllocationValidationRunDetailRule) => {
  if (isFailedRuleStatus(rule.status)) return '';
  if (normalizeRuleStatus(rule.status) !== 'completed') return '';
  if (rule.validationResult) return rule.validationResult;
  return parseCount(rule.failed) > 0 ? 'Needs Review' : 'All Good';
};
const getRuleValidationResultClass = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'all good') return 'bg-success-100 text-success-700';
  if (normalized === 'needs review') return 'bg-error-50 text-error-700';
  if (normalized === 'run failed') return 'bg-error-50 text-error-700';
  return 'bg-warning-50 text-warning-700';
};
const buildRuleDownloadFileName = (runName: string, rule: AllocationValidationRunDetailRule) => {
  const safeRunName = runName.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'allocation_run';
  const safeRuleId = rule.ruleId.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'rule';
  const safeStep = rule.allocStep.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'step';
  const safeKpi = (rule.ratioKpi || rule.allocKpi || 'kpi').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'kpi';
  const safeRuleName = rule.rule.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || `rule_${rule.ruleId}`;
  return `${safeRunName}_${safeRuleId}_${safeStep}_${safeKpi}_${safeRuleName}.xlsx`;
};
const getRuleNumberOfKeys = (rule: AllocationValidationRunDetailRule) => {
  if (rule.numberOfKeys) return rule.numberOfKeys;
  const countedKeys = parseCount(rule.passed) + parseCount(rule.failed) + parseCount(rule.audit);
  if (countedKeys > 0) return String(countedKeys);
  return rule.totalRows;
};
const runDetailRuleColumns: Array<{
  apiKey: string;
  header: string;
  tooltip: string;
  value: (rule: AllocationValidationRunDetailRule) => string;
  align?: 'left' | 'center';
  className?: string;
}> = [
  { apiKey: 'rule', header: 'Rule', tooltip: 'Allocation rule being validated.', value: (rule) => rule.rule, className: 'min-w-[15rem]' },
  { apiKey: 'source', header: 'Source', tooltip: 'Source system for the rule, such as CO-PA or G/L.', value: (rule) => rule.source, className: 'min-w-[6rem]' },
  { apiKey: 'alloc_step', header: 'Alloc Step', tooltip: 'Rule step: ratio calculation, to-be-allocated validation, or allocation validation.', value: (rule) => rule.allocStep, className: 'min-w-[10rem]' },
  { apiKey: 'ratio_kpi', header: 'Ratio KPI', tooltip: 'KPI used to calculate allocation ratios. Blank when the rule does not calculate or use a ratio KPI.', value: (rule) => rule.ratioKpi, className: 'min-w-[11rem]' },
  { apiKey: 'alloc_kpi', header: 'Alloc KPI', tooltip: 'Amount being validated or allocated for this rule.', value: (rule) => rule.allocKpi, className: 'min-w-[13rem]' },
  { apiKey: 'number_of_keys', header: 'Number of Keys', tooltip: 'Total keys validated for this rule. Includes Passed, Failed, and Audit keys.', value: getRuleNumberOfKeys, align: 'center', className: 'min-w-[9.5rem]' },
  { apiKey: 'passed', header: 'Passed', tooltip: 'Keys where the validation passed.', value: (rule) => rule.passed, align: 'center', className: 'min-w-[7rem]' },
  { apiKey: 'failed', header: 'Failed', tooltip: 'Keys that failed validation and need review.', value: (rule) => rule.failed, align: 'center', className: 'min-w-[7rem]' },
  { apiKey: 'audit', header: 'Audit', tooltip: 'Informational keys kept for audit. These do not make the rule Needs Review.', value: (rule) => rule.audit, align: 'center', className: 'min-w-[7rem]' },
  { apiKey: 'validation_result', header: 'Result', tooltip: 'High-level validation outcome based on failed count. Failed > 0 means Needs Review.', value: getRuleValidationResult, align: 'center', className: 'min-w-[9rem]' },
  { apiKey: 'status', header: 'Run Status', tooltip: 'Execution status of this rule run.', value: (rule) => rule.status, align: 'center', className: 'min-w-[8rem]' },
];
const getColumnAlignClass = (align: 'left' | 'center' = 'left') => (
  align === 'center' ? 'text-center' : 'text-left'
);
const getColumnHeaderAlignClass = (align: 'left' | 'center' = 'left') => (
  align === 'center' ? 'justify-center' : 'justify-start'
);
const getRunDetailRuleHeaderClass = (apiKey: string) => (
  apiKey === 'validation_result'
    ? 'sticky right-[12.5rem] z-20 bg-muted shadow-[-1px_0_0_hsl(var(--border))]'
    : apiKey === 'status'
    ? 'sticky right-[4.5rem] z-20 bg-muted shadow-[-1px_0_0_hsl(var(--border))]'
    : ''
);

const getRunDetailRuleCellClass = (apiKey: string) => (
  apiKey === 'validation_result'
    ? 'sticky right-[12.5rem] z-10 bg-card text-center shadow-[-1px_0_0_hsl(var(--border))] group-hover:bg-accent/60'
    : apiKey === 'status'
    ? 'sticky right-[4.5rem] z-10 bg-card text-center shadow-[-1px_0_0_hsl(var(--border))] group-hover:bg-accent/60'
    : ''
);

const mergeRulesById = (currentRules: AllocationRule[], nextRules: AllocationRule[]) => {
  const merged = new Map<string, AllocationRule>();
  currentRules.forEach((rule) => merged.set(rule.id, rule));
  nextRules.forEach((rule) => merged.set(rule.id, rule));
  return Array.from(merged.values());
};

export const AllocationsDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useGlobalToast();
  const [pageSize, setPageSize] = React.useState(15);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [allocationRuns, setAllocationRuns] = React.useState<AllocationRun[]>([]);
  const [allocationRunsTotal, setAllocationRunsTotal] = React.useState(0);
  const [isLoadingRuns, setIsLoadingRuns] = React.useState(true);
  const [runsError, setRunsError] = React.useState<string | null>(null);
  const [runsSearchTerm, setRunsSearchTerm] = React.useState('');
  const [appliedRunsSearchTerm, setAppliedRunsSearchTerm] = React.useState('');
  const [runStatusFilter, setRunStatusFilter] = React.useState<AllocationValidationRunStatusFilter>('all');
  const [sapConnectionStatus, setSapConnectionStatus] = React.useState<AllocationSapConnectionStatus | null>(null);
  const [isCheckingSapConnection, setIsCheckingSapConnection] = React.useState(true);
  const [sapConnectionError, setSapConnectionError] = React.useState<string | null>(null);
  const [selectedRun, setSelectedRun] = React.useState<AllocationRun | null>(null);
  const [runDetails, setRunDetails] = React.useState<AllocationValidationRunDetail | null>(null);
  const [isLoadingRunDetails, setIsLoadingRunDetails] = React.useState(false);
  const [runDetailsError, setRunDetailsError] = React.useState<string | null>(null);
  const [ruleResultsSearchTerm, setRuleResultsSearchTerm] = React.useState('');
  const [ruleResultsStatusFilter, setRuleResultsStatusFilter] = React.useState<RuleResultStatusFilter>('all');
  const [ruleResultsResultFilter, setRuleResultsResultFilter] = React.useState<RuleValidationResultFilter>('all');
  const [rulesPopoverRun, setRulesPopoverRun] = React.useState<AllocationRun | null>(null);
  const [rulesPopoverRules, setRulesPopoverRules] = React.useState<AllocationValidationRunDetailRule[]>([]);
  const [isLoadingRulesPopover, setIsLoadingRulesPopover] = React.useState(false);
  const [rulesPopoverError, setRulesPopoverError] = React.useState<string | null>(null);
  const [rulesPopoverPosition, setRulesPopoverPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [downloadingRuleId, setDownloadingRuleId] = React.useState<string | null>(null);
  const [allocationRules, setAllocationRules] = React.useState<AllocationRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = React.useState(false);
  const [rulesError, setRulesError] = React.useState<string | null>(null);
  const [rulesTotal, setRulesTotal] = React.useState(0);
  const [rulesNextOffset, setRulesNextOffset] = React.useState<number | null>(null);
  const [rulesHasMore, setRulesHasMore] = React.useState(false);
  const [rulesSearchTerm, setRulesSearchTerm] = React.useState('');
  const [isCreateRunOpen, setIsCreateRunOpen] = React.useState(false);
  const [runTitle, setRunTitle] = React.useState('');
  const [runDescription, setRunDescription] = React.useState('');
  const [runPeriod, setRunPeriod] = React.useState('');
  const [selectedRuleIds, setSelectedRuleIds] = React.useState<string[]>([]);
  const [selectedRulesById, setSelectedRulesById] = React.useState<Record<string, AllocationRule>>({});
  const [hasAttemptedCreate, setHasAttemptedCreate] = React.useState(false);
  const [isCreatingRun, setIsCreatingRun] = React.useState(false);
  const [isConnectingSap, setIsConnectingSap] = React.useState(false);
  const runsRequestIdRef = React.useRef(0);
  const runsInFlightKeyRef = React.useRef<string | null>(null);
  const runsInFlightRef = React.useRef<Promise<void> | null>(null);
  const runsPollingIntervalRef = React.useRef<number | null>(null);
  const isRunsPollingRequestActiveRef = React.useRef(false);
  const watchedAllocationRunsRef = React.useRef<Record<string, WatchedAllocationRun>>({});
  const rulesRequestIdRef = React.useRef(0);
  const rulesPopoverRequestIdRef = React.useRef(0);
  const sapConnectionRequestIdRef = React.useRef(0);
  const sapConnectionInFlightRef = React.useRef<Promise<AllocationSapConnectionStatus | null> | null>(null);
  const hasRequestedCreateRulesRef = React.useRef(false);
  const handledSapCallbackResultRef = React.useRef<string | null>(null);
  const sapExchange = searchParams.get('sap_exchange');
  const sapError = searchParams.get('sap_error');
  const sapCallbackMessage = searchParams.get('message') || '';
  const hasSapCallbackResult = Boolean(sapExchange || sapError);

  const clearRunsPollingInterval = React.useCallback(() => {
    if (runsPollingIntervalRef.current === null) return;
    window.clearInterval(runsPollingIntervalRef.current);
    runsPollingIntervalRef.current = null;
    isRunsPollingRequestActiveRef.current = false;
  }, []);

  const loadSapConnectionStatus = React.useCallback((): Promise<AllocationSapConnectionStatus | null> => {
    if (sapConnectionInFlightRef.current) {
      return sapConnectionInFlightRef.current;
    }

    const requestId = sapConnectionRequestIdRef.current + 1;
    sapConnectionRequestIdRef.current = requestId;
    setIsCheckingSapConnection(true);
    setSapConnectionError(null);

    const request = (async () => {
      try {
        const status = await allocationValidationApi.getSapConnectionStatus();
        if (requestId !== sapConnectionRequestIdRef.current) return null;
        setSapConnectionStatus(status);
        return status;
      } catch (error) {
        if (requestId !== sapConnectionRequestIdRef.current) return null;
        setSapConnectionStatus(null);
        setSapConnectionError(getApiErrorMessage(error, 'Unable to check SAP connection.'));
        return null;
      } finally {
        if (requestId === sapConnectionRequestIdRef.current) {
          setIsCheckingSapConnection(false);
        }
      }
    })();

    sapConnectionInFlightRef.current = request;
    void request.finally(() => {
      if (sapConnectionInFlightRef.current === request) {
        sapConnectionInFlightRef.current = null;
      }
    });
    return request;
  }, []);

  React.useEffect(() => {
    if (hasSapCallbackResult) return;
    void loadSapConnectionStatus();
  }, [hasSapCallbackResult, loadSapConnectionStatus]);

  React.useEffect(() => {
    if (!hasSapCallbackResult) return;

    const resultKey = `${sapExchange || ''}:${sapError || ''}:${sapCallbackMessage}`;
    if (handledSapCallbackResultRef.current === resultKey) return;
    handledSapCallbackResultRef.current = resultKey;

    const cleanedParams = new URLSearchParams(searchParams);
    cleanedParams.delete('sap_exchange');
    cleanedParams.delete('sap_error');
    cleanedParams.delete('message');
    const cleanedQuery = cleanedParams.toString();
    navigate(cleanedQuery ? `/allocations?${cleanedQuery}` : '/allocations', { replace: true });

    if (sapError) {
      void loadSapConnectionStatus();
      toastError('Connection Failed', sapCallbackMessage || 'SAP Datasphere connection failed. Please try again.');
      return;
    }

    void (async () => {
      const status = await loadSapConnectionStatus();
      if (status?.connected) {
        toastSuccess('Connected to data source');
        return;
      }
      toastError("Couldn't connect to data source", 'Please try reconnecting.');
    })();
  }, [
    hasSapCallbackResult,
    loadSapConnectionStatus,
    navigate,
    sapCallbackMessage,
    sapError,
    sapExchange,
    searchParams,
    toastError,
    toastSuccess,
  ]);

  const loadAllocationRuns = React.useCallback(async ({
    search = '',
    status = 'all',
    page = 1,
    size = PAGE_SIZE_OPTIONS[0],
    silent = false,
  }: {
    search?: string;
    status?: AllocationValidationRunStatusFilter;
    page?: number;
    size?: number;
    silent?: boolean;
  } = {}) => {
    const requestKey = JSON.stringify({ search, status, page, size, silent });
    if (runsInFlightKeyRef.current === requestKey && runsInFlightRef.current) {
      return runsInFlightRef.current;
    }

    const requestId = runsRequestIdRef.current + 1;
    runsRequestIdRef.current = requestId;
    if (!silent) {
      setIsLoadingRuns(true);
      setRunsError(null);
    }

    const request = (async () => {
      try {
        const result = await allocationValidationApi.listRuns({
          search,
          status,
          limit: size,
          offset: (page - 1) * size,
        });
        if (requestId !== runsRequestIdRef.current) return;
        result.runs.forEach((run) => {
          const watchedRun = watchedAllocationRunsRef.current[run.id];
          if (!watchedRun || watchedRun.completionNotified || run.status !== 'Completed') return;

          watchedAllocationRunsRef.current[run.id] = {
            ...watchedRun,
            completionNotified: true,
          };
          const message = `"${run.name || watchedRun.name}" completed successfully. You can review the results now.`;
          toastSuccess('Run completed', message);
        });
        setAllocationRuns(result.runs);
        setAllocationRunsTotal(result.total);
      } catch (error) {
        if (requestId !== runsRequestIdRef.current) return;
        if (!silent) {
          setAllocationRuns([]);
          setAllocationRunsTotal(0);
          setRunsError(getApiErrorMessage(error, 'Unable to load runs.'));
        }
      } finally {
        if (!silent && requestId === runsRequestIdRef.current) {
          setIsLoadingRuns(false);
        }
      }
    })();

    runsInFlightKeyRef.current = requestKey;
    runsInFlightRef.current = request;
    void request.finally(() => {
      if (runsInFlightRef.current === request) {
        runsInFlightRef.current = null;
        runsInFlightKeyRef.current = null;
      }
    });
    return request;
  }, [toastSuccess]);

  React.useEffect(() => {
    void loadAllocationRuns({
      search: appliedRunsSearchTerm,
      status: runStatusFilter,
      page: currentPage,
      size: pageSize,
    });
  }, [appliedRunsSearchTerm, currentPage, loadAllocationRuns, pageSize, runStatusFilter]);

  const submitRunsSearch = () => {
    const trimmedSearch = runsSearchTerm.trim();
    if (trimmedSearch.length > 0 && trimmedSearch.length < 2) return;

    setCurrentPage(1);
    setAppliedRunsSearchTerm(trimmedSearch);
  };

  const clearRunsSearch = () => {
    setRunsSearchTerm('');
    if (appliedRunsSearchTerm) {
      setCurrentPage(1);
      setAppliedRunsSearchTerm('');
    }
  };

  const handleRunStatusFilterChange = (value: string) => {
    setRunStatusFilter(value as AllocationValidationRunStatusFilter);
    setCurrentPage(1);
  };

  const loadAllocationRules = React.useCallback(async ({
    search = '',
    offset = 0,
    append = false,
  }: {
    search?: string;
    offset?: number;
    append?: boolean;
  } = {}) => {
    const requestId = rulesRequestIdRef.current + 1;
    rulesRequestIdRef.current = requestId;
    setIsLoadingRules(true);
    setRulesError(null);

    try {
      const result = await allocationValidationApi.listRulesPage({
        search,
        limit: RULES_PAGE_SIZE,
        offset,
      });
      if (requestId !== rulesRequestIdRef.current) return;

      setAllocationRules((currentRules) => (
        append ? mergeRulesById(currentRules, result.rules) : result.rules
      ));
      setRulesTotal(result.total);
      setRulesNextOffset(result.nextOffset);
      setRulesHasMore(result.hasMore);
      setRulesSearchTerm(search);
    } catch (error) {
      if (requestId !== rulesRequestIdRef.current) return;
      if (!append) setAllocationRules([]);
      setRulesError(getApiErrorMessage(error, 'Unable to load rules.'));
    } finally {
      if (requestId === rulesRequestIdRef.current) {
        setIsLoadingRules(false);
      }
    }
  }, []);

  const searchAllocationRules = React.useCallback((searchTerm: string) => {
    void loadAllocationRules({ search: searchTerm, offset: 0, append: false });
  }, [loadAllocationRules]);

  const loadMoreAllocationRules = React.useCallback((searchTerm: string) => {
    if (!rulesHasMore || rulesNextOffset === null || isLoadingRules) return;
    void loadAllocationRules({
      search: searchTerm,
      offset: rulesNextOffset,
      append: true,
    });
  }, [isLoadingRules, loadAllocationRules, rulesHasMore, rulesNextOffset]);

  const handleSelectedRulesChange = React.useCallback((
    values: string[],
    selectedOptions: AllocationRule[],
  ) => {
    setSelectedRuleIds(values);
    setSelectedRulesById((currentRulesById) => {
      const nextRulesById: Record<string, AllocationRule> = {};

      values.forEach((ruleId) => {
        const rule = (
          selectedOptions.find((option) => option.id === ruleId) ||
          allocationRules.find((option) => option.id === ruleId) ||
          currentRulesById[ruleId]
        );

        if (rule) nextRulesById[ruleId] = rule;
      });

      return nextRulesById;
    });
  }, [allocationRules]);

  const selectedRules = React.useMemo(() => (
    selectedRuleIds
      .map((ruleId) => (
        selectedRulesById[ruleId] ||
        allocationRules.find((rule) => rule.id === ruleId)
      ))
      .filter((rule): rule is AllocationRule => Boolean(rule))
  ), [allocationRules, selectedRuleIds, selectedRulesById]);

  const canShowCreateRunForm = sapConnectionStatus?.connected === true;

  const isCreateRunDisabled = (
    !canShowCreateRunForm ||
    !runTitle.trim() ||
    !runPeriod.trim() ||
    selectedRuleIds.length === 0 ||
    isLoadingRules ||
    isCreatingRun ||
    Boolean(rulesError)
  );

  const totalPages = Math.max(1, Math.ceil(allocationRunsTotal / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const visibleRuns = allocationRuns;
  const pageWindowStart = Math.min(
    Math.max(safeCurrentPage - 1, 1),
    Math.max(totalPages - 2, 1),
  );
  const visiblePageNumbers = Array.from(
    { length: Math.min(3, totalPages) },
    (_, index) => pageWindowStart + index,
  );
  const filteredRunDetailRules = React.useMemo(() => {
    const rules = runDetails?.rules ?? [];
    const normalizedSearch = ruleResultsSearchTerm.trim().toLowerCase();

    return rules.filter((rule) => {
      const ruleStatus = normalizeRuleStatus(rule.status);
      if (ruleResultsStatusFilter !== 'all' && ruleStatus !== ruleResultsStatusFilter) {
        return false;
      }

      const validationResult = getRuleValidationResult(rule);
      const validationResultKey: RuleValidationResultFilter = validationResult
        ? (validationResult.trim().toLowerCase() === 'all good' ? 'all_good' : 'needs_review')
        : 'none';
      if (ruleResultsResultFilter !== 'all' && validationResultKey !== ruleResultsResultFilter) {
        return false;
      }

      if (!normalizedSearch) return true;

      return [
        rule.ruleId,
        rule.rule,
        rule.source,
        rule.allocStep,
        rule.ratioKpi,
        rule.allocKpi,
        getRuleNumberOfKeys(rule),
        rule.passed,
        rule.failed,
        rule.audit,
        validationResult,
        formatRuleStatus(rule.status),
      ].some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [ruleResultsResultFilter, ruleResultsSearchTerm, ruleResultsStatusFilter, runDetails]);
  const hasRuleResultFilters = (
    ruleResultsSearchTerm.trim() !== '' ||
    ruleResultsStatusFilter !== 'all' ||
    ruleResultsResultFilter !== 'all'
  );

  React.useEffect(() => {
    clearRunsPollingInterval();

    if (
      runsError ||
      visibleRuns.length === 0 ||
      visibleRuns.every((run) => isRunPollingTerminal(run.status))
    ) {
      return;
    }

    runsPollingIntervalRef.current = window.setInterval(() => {
      if (isRunsPollingRequestActiveRef.current) return;

      isRunsPollingRequestActiveRef.current = true;
      void loadAllocationRuns({
        search: appliedRunsSearchTerm,
        status: runStatusFilter,
        page: currentPage,
        size: pageSize,
        silent: true,
      }).finally(() => {
        isRunsPollingRequestActiveRef.current = false;
      });
    }, RUNS_POLLING_INTERVAL_MS);

    return clearRunsPollingInterval;
  }, [
    appliedRunsSearchTerm,
    clearRunsPollingInterval,
    currentPage,
    loadAllocationRuns,
    pageSize,
    runStatusFilter,
    runsError,
    visibleRuns,
  ]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const closeCreateRunModal = () => {
    rulesRequestIdRef.current += 1;
    setIsCreateRunOpen(false);
    setRunTitle('');
    setRunDescription('');
    setRunPeriod('');
    setSelectedRuleIds([]);
    setSelectedRulesById({});
    setAllocationRules([]);
    setRulesError(null);
    setIsLoadingRules(false);
    setRulesTotal(0);
    setRulesNextOffset(null);
    setRulesHasMore(false);
    setRulesSearchTerm('');
    setHasAttemptedCreate(false);
    setIsCreatingRun(false);
    setIsConnectingSap(false);
    hasRequestedCreateRulesRef.current = false;
  };

  const openCreateRunModal = () => {
    setIsCreateRunOpen(true);
    setSelectedRuleIds([]);
    setSelectedRulesById({});
    setRulesError(null);
    setRulesSearchTerm('');
    hasRequestedCreateRulesRef.current = false;
    if (!sapConnectionStatus || sapConnectionError) {
      void loadSapConnectionStatus();
    }
  };

  React.useEffect(() => {
    if (!isCreateRunOpen || !canShowCreateRunForm || hasRequestedCreateRulesRef.current) return;
    hasRequestedCreateRulesRef.current = true;
    void loadAllocationRules({ search: '', offset: 0, append: false });
  }, [canShowCreateRunForm, isCreateRunOpen, loadAllocationRules]);

  const handleConnectDataSource = async () => {
    if (isConnectingSap) return;

    try {
      setIsConnectingSap(true);
      await sapOAuthService.initiateConnection('allocations');
    } catch (error) {
      setIsConnectingSap(false);
      toastError('Connection Failed', getApiErrorMessage(error, 'Failed to initiate SAP connection.'));
    }
  };

  const openRunDetails = (run: AllocationRun) => {
    if (!isRunDetailsEligible(run.status)) return;
    setSelectedRun(run);
  };

  const closeRunDetails = () => {
    setSelectedRun(null);
    setRunDetails(null);
    setRunDetailsError(null);
    setIsLoadingRunDetails(false);
    setDownloadingRuleId(null);
    setRuleResultsSearchTerm('');
    setRuleResultsStatusFilter('all');
    setRuleResultsResultFilter('all');
  };

  const closeRulesPopover = React.useCallback(() => {
    rulesPopoverRequestIdRef.current += 1;
    setRulesPopoverRun(null);
    setRulesPopoverRules([]);
    setRulesPopoverError(null);
    setIsLoadingRulesPopover(false);
    setRulesPopoverPosition(null);
  }, []);

  const loadRulesPopover = React.useCallback(async (run: AllocationRun) => {
    const requestId = rulesPopoverRequestIdRef.current + 1;
    rulesPopoverRequestIdRef.current = requestId;
    setIsLoadingRulesPopover(true);
    setRulesPopoverError(null);
    setRulesPopoverRules([]);

    try {
      const details = await allocationValidationApi.getRunDetails(run.id);
      if (requestId !== rulesPopoverRequestIdRef.current) return;
      setRulesPopoverRules(details.rules);
    } catch (error) {
      if (requestId !== rulesPopoverRequestIdRef.current) return;
      setRulesPopoverError(getApiErrorMessage(error, 'Unable to load rules for this run.'));
    } finally {
      if (requestId === rulesPopoverRequestIdRef.current) {
        setIsLoadingRulesPopover(false);
      }
    }
  }, []);

  const openRulesPopover = (run: AllocationRun, anchorElement: HTMLElement) => {
    const rect = anchorElement.getBoundingClientRect();
    const viewportPadding = 16;
    const gap = 12;
    const panelWidth = Math.min(416, window.innerWidth - viewportPadding * 2);
    const rowCount = Math.max(run.totalRules || 1, 1);
    const estimatedPanelHeight = Math.min(320, 76 + Math.min(rowCount, 4) * 58);
    const panelMaxHeight = Math.min(320, window.innerHeight - viewportPadding * 2);
    const maxTop = Math.max(window.innerHeight - estimatedPanelHeight - viewportPadding, viewportPadding);
    const top = Math.min(Math.max(rect.top - 12, viewportPadding), maxTop);
    const leftOfIcon = rect.left - panelWidth - gap;
    const rightOfIcon = rect.right + gap;
    const preferredLeft = leftOfIcon >= viewportPadding ? leftOfIcon : rightOfIcon;
    const maxLeft = Math.max(window.innerWidth - panelWidth - viewportPadding, viewportPadding);
    const left = Math.min(Math.max(preferredLeft, viewportPadding), maxLeft);

    setRulesPopoverRun(run);
    setRulesPopoverPosition({
      top,
      left,
      width: panelWidth,
      maxHeight: panelMaxHeight,
    });
    void loadRulesPopover(run);
  };

  React.useEffect(() => {
    if (!rulesPopoverRun) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRulesPopover();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeRulesPopover, rulesPopoverRun]);

  React.useEffect(() => {
    if (!selectedRun) return;

    let isCancelled = false;
    setRunDetails(null);
    setRunDetailsError(null);
    setRuleResultsSearchTerm('');
    setRuleResultsStatusFilter('all');
    setRuleResultsResultFilter('all');
    setIsLoadingRunDetails(true);

    const loadRunDetails = async () => {
      try {
        const details = await allocationValidationApi.getRunDetails(selectedRun.id);
        if (isCancelled) return;
        setRunDetails(details);
      } catch (error) {
        if (isCancelled) return;
        setRunDetailsError(getApiErrorMessage(error, 'Unable to load run details.'));
      } finally {
        if (!isCancelled) {
          setIsLoadingRunDetails(false);
        }
      }
    };

    void loadRunDetails();

    return () => {
      isCancelled = true;
    };
  }, [selectedRun]);

  const handleCreateRun = async () => {
    setHasAttemptedCreate(true);
    if (isCreateRunDisabled) return;

    setIsCreatingRun(true);
    try {
      const title = runTitle.trim();
      const period = runPeriod.trim();
      const selectedRuleCount = selectedRuleIds.length;

      const createdRun = await allocationValidationApi.createRun({
        title,
        description: runDescription.trim(),
        period,
        ruleIds: selectedRuleIds,
      });
      if (createdRun.runId) {
        watchedAllocationRunsRef.current[createdRun.runId] = {
          name: title,
          period,
          completionNotified: false,
        };
      }
      const inProgressMessage = `"${title}" is in progress. This can take a few minutes, so please come back later to review the results.`;
      toastSuccess(
        'Run in progress',
        inProgressMessage,
      );
      closeCreateRunModal();
      setCurrentPage(1);
      await loadAllocationRuns({
        search: appliedRunsSearchTerm,
        status: runStatusFilter,
        page: 1,
        size: pageSize,
      });
    } catch (error) {
      toastError('Create Run Failed', getApiErrorMessage(error, 'Unable to create run.'));
    } finally {
      setIsCreatingRun(false);
    }
  };

  const handleDownloadRuleResult = async (rule: AllocationValidationRunDetailRule) => {
    if (!rule.blobUrl || downloadingRuleId) return;
    const runName = runDetails?.name || selectedRun?.name || 'allocation_run';
    setDownloadingRuleId(rule.ruleId);
    try {
      await allocationValidationApi.downloadRuleResult(rule.blobUrl, buildRuleDownloadFileName(runName, rule));
    } catch (error) {
      toastError('Download Failed', getApiErrorMessage(error, 'Unable to download this result file.'));
    } finally {
      setDownloadingRuleId(null);
    }
  };

  return (
    <div className="min-h-full bg-background px-6 py-6 text-foreground lg:px-8">
      <div className="space-y-7">
        <header>
          <h1 className="text-2xl font-bold leading-tight tracking-normal text-foreground">Allocations</h1>
          <p className="mt-2 text-base leading-6 text-muted-foreground">
            Manage allocation planning and financial distribution workflows.
          </p>
        </header>

        {SHOW_ALLOCATION_KPIS && (
          <section className="grid grid-cols-1 gap-4 md:grid-cols-3 min-[1200px]:grid-cols-5">
            {allocationKpis.map((kpi) => {
              const Icon = kpiIcons[kpi.id];
              const isNegativeTrend = kpi.id === 'failed-runs';
              const trendClass = isNegativeTrend ? 'text-error-700' : 'text-success-700';
              const TrendIcon = kpi.trendDirection === 'up' ? ArrowUp : ArrowDown;

              return (
                <article
                  key={kpi.id}
                  className="relative min-h-[6rem] overflow-hidden rounded-xl border border-border bg-card px-4 py-4 shadow-[0_10px_24px_rgba(92,39,81,0.08)]"
                >
                  <div className="pointer-events-none absolute -bottom-12 left-8 h-24 w-24 rounded-full bg-primary/5" />

                  <div className="relative z-10 flex h-full flex-col justify-between gap-1">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                        <Icon className="h-[1.125rem] w-[1.125rem]" />
                      </div>
                      <p className="min-w-0 whitespace-nowrap text-xs font-semibold leading-4 text-muted-foreground">
                        {kpi.label}
                      </p>
                    </div>

                    <p className="text-[1.65rem] font-bold leading-none tracking-normal text-foreground">
                      {kpi.value}
                    </p>

                    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 pr-14 text-xs leading-4 text-muted-foreground">
                      {kpi.trendText ? (
                        <>
                          <span className={`inline-flex items-center gap-1 font-medium ${trendClass}`}>
                            <TrendIcon className="h-3.5 w-3.5" />
                            {kpi.trendText}
                          </span>
                          <span>{kpi.helperText}</span>
                        </>
                      ) : (
                        <span>{kpi.helperText}</span>
                      )}
                    </p>
                  </div>

                  <div className="pointer-events-none absolute bottom-4 right-3 opacity-45">
                    <AllocationSparkline variant={kpi.visual} kpiId={kpi.id} />
                  </div>
                </article>
              );
            })}
          </section>
        )}

        <div className="space-y-3">
          <section className="flex flex-col gap-3 pt-1 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="text-xl font-bold leading-none tracking-normal text-foreground">Runs</h2>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:justify-end">
              <label className="relative block h-9 w-full sm:w-60">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={runsSearchTerm}
                  onChange={(event) => setRunsSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      submitRunsSearch();
                    }
                  }}
                  placeholder="Search runs"
                  title="Enter at least 2 characters and press Enter to search"
                  className="h-9 w-full rounded-md border border-border bg-card pl-10 pr-9 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                />
                {runsSearchTerm && (
                  <button
                    type="button"
                    onClick={clearRunsSearch}
                    className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    title="Clear search"
                    aria-label="Clear runs search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </label>

              <select
                value={runStatusFilter}
                onChange={(event) => handleRunStatusFilterChange(event.target.value)}
                aria-label="Filter runs by status"
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm outline-none transition-colors focus:border-primary sm:w-40"
              >
                {RUN_STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

            <ActionTooltip content="Refresh">
              <button
                type="button"
                onClick={() => void loadAllocationRuns({
                  search: appliedRunsSearchTerm,
                  status: runStatusFilter,
                  page: currentPage,
                  size: pageSize,
                })}
                disabled={isLoadingRuns}
                aria-label="Refresh runs"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${isLoadingRuns ? 'animate-spin' : ''}`} />
              </button>
            </ActionTooltip>

            <button
              type="button"
              onClick={openCreateRunModal}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Create
            </button>
            </div>
          </section>

          <section className="space-y-2">
          {isLoadingRuns ? (
            <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-sm font-medium text-muted-foreground shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
              Loading runs...
            </div>
          ) : runsError ? (
            <div className="rounded-lg border border-error-100 bg-error-50 px-4 py-5 text-sm text-error-700 shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span>{runsError}</span>
                <button
                  type="button"
                  onClick={() => void loadAllocationRuns({
                    search: appliedRunsSearchTerm,
                    status: runStatusFilter,
                    page: currentPage,
                    size: pageSize,
                  })}
                  className="inline-flex h-8 items-center justify-center rounded-md border border-error-200 bg-card px-3 text-xs font-semibold text-error-700 shadow-sm hover:bg-error-50"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : visibleRuns.length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-sm font-medium text-muted-foreground shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
              No runs found.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-[0_8px_22px_rgba(15,23,42,0.05)]">
              <div className="max-h-[calc(100vh-19rem)] overflow-auto">
                <table className="w-max min-w-full border-collapse text-left">
                  <thead className="sticky top-0 z-10 bg-muted text-xs font-semibold text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="min-w-[10rem] max-w-[14rem] px-4 py-3">Name</th>
                      <th className="min-w-[13rem] max-w-[24rem] px-4 py-3">Description</th>
                      <th className="min-w-[13rem] max-w-[18rem] px-4 py-3">Created By</th>
                      <th className="min-w-[6rem] whitespace-nowrap px-3 py-3 text-center">Period</th>
                      <th className="min-w-[4rem] whitespace-nowrap px-3 py-3 text-center">Rules</th>
                      <th className="min-w-[11.5rem] whitespace-nowrap px-3 py-3 text-center">Started At</th>
                      <th className="min-w-[11.5rem] whitespace-nowrap px-3 py-3 text-center">Completed At</th>
                      <th className="min-w-[8rem] whitespace-nowrap px-3 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-[0.78rem] text-foreground">
                    {visibleRuns.map((run) => {
                      const canViewResults = isRunDetailsEligible(run.status);

                      return (
                        <tr
                          key={run.id}
                          onClick={() => openRunDetails(run)}
                          className={`border-b border-border/70 last:border-b-0 hover:bg-accent/60 ${
                            canViewResults ? 'cursor-pointer' : 'cursor-default'
                          }`}
                        >
                          <td className="max-w-[14rem] px-4 py-3 align-top">
                            <span className="block break-words text-[0.8rem] font-bold leading-5 text-foreground">
                              {run.name}
                            </span>
                          </td>
                          <td className="max-w-[24rem] px-4 py-3 align-top">
                            <span className="block break-words text-[0.78rem] leading-5 text-muted-foreground">
                              {run.description || '-'}
                            </span>
                          </td>
                          <td className="max-w-[18rem] px-4 py-3 align-top">
                            <span className="block break-words text-[0.78rem] leading-5 text-foreground">
                              {run.createdBy || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center align-top text-[0.78rem] leading-5 text-foreground">
                            {run.period || '-'}
                          </td>
                          <td className="px-3 py-3 text-center align-top">
                            <button
                              type="button"
                              aria-label={`View rules for ${run.name}`}
                              title="View rules"
                              onClick={(event) => {
                                event.stopPropagation();
                                openRulesPopover(run, event.currentTarget);
                              }}
                              className="inline-flex min-w-7 items-center justify-center rounded-md px-2 py-1 text-[0.78rem] font-semibold text-primary transition-colors hover:bg-accent"
                            >
                              {run.totalRules}
                            </button>
                          </td>
                          <td className="min-w-[11.5rem] whitespace-nowrap px-3 py-3 text-center align-top text-[0.78rem] leading-5 text-foreground">
                            {formatRunDate(run.createdAt)}
                          </td>
                          <td className="min-w-[11.5rem] whitespace-nowrap px-3 py-3 text-center align-top text-[0.78rem] leading-5 text-foreground">
                            {formatRunDate(run.completedAt)}
                          </td>
                          <td className="px-3 py-3 text-center align-top">
                            <span className={`inline-flex min-w-[86px] justify-center rounded-md px-2.5 py-1 text-xs font-semibold ${statusStyles[run.status]}`}>
                              {run.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4 pt-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {visibleRuns.length} of {allocationRunsTotal} runs
            </p>

            <div className="flex flex-wrap items-center gap-1 lg:justify-end">
              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setCurrentPage(1);
                }}
                aria-label="Runs per page"
                className="h-8 w-16 rounded-md border border-border bg-card px-2 text-sm font-medium text-foreground shadow-sm outline-none"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <PaginationButton
                ariaLabel="First page"
                disabled={safeCurrentPage === 1 || allocationRunsTotal === 0}
                onClick={() => setCurrentPage(1)}
              >
                <ChevronsLeft className="h-3.5 w-3.5 text-muted-foreground" />
              </PaginationButton>
              <PaginationButton
                ariaLabel="Previous page"
                disabled={safeCurrentPage === 1 || allocationRunsTotal === 0}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
              </PaginationButton>
              {visiblePageNumbers.map((pageNumber) => (
                <PaginationButton
                  key={pageNumber}
                  active={pageNumber === safeCurrentPage}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </PaginationButton>
              ))}
              <PaginationButton
                ariaLabel="Next page"
                disabled={safeCurrentPage === totalPages || allocationRunsTotal === 0}
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </PaginationButton>
              <PaginationButton
                ariaLabel="Last page"
                disabled={safeCurrentPage === totalPages || allocationRunsTotal === 0}
                onClick={() => setCurrentPage(totalPages)}
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </PaginationButton>
            </div>
          </div>
          </section>
        </div>
      </div>

      {rulesPopoverRun && rulesPopoverPosition && (
        <div
          className="fixed inset-0 z-[90]"
          onMouseDown={closeRulesPopover}
        >
          <div
            role="dialog"
            aria-modal="false"
            aria-labelledby="run-rules-popover-title"
            className="absolute flex max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-[0_18px_45px_rgba(15,23,42,0.18)]"
            style={{
              top: rulesPopoverPosition.top,
              left: rulesPopoverPosition.left,
              width: rulesPopoverPosition.width,
              maxHeight: rulesPopoverPosition.maxHeight,
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <h3 id="run-rules-popover-title" className="text-sm font-semibold text-popover-foreground">
                Rules in this Run ({isLoadingRulesPopover ? rulesPopoverRun.totalRules : rulesPopoverRules.length})
              </h3>
              <button
                type="button"
                aria-label="Close rules list"
                onClick={closeRulesPopover}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {isLoadingRulesPopover ? (
              <div className="px-4 py-5 text-sm font-medium text-muted-foreground">
                Loading rules...
              </div>
            ) : rulesPopoverError ? (
              <div className="space-y-3 px-4 py-4">
                <p className="text-sm text-error-700">{rulesPopoverError}</p>
                <button
                  type="button"
                  onClick={() => void loadRulesPopover(rulesPopoverRun)}
                  className="inline-flex h-8 items-center justify-center rounded-md border border-error-200 bg-card px-3 text-xs font-semibold text-error-700 hover:bg-error-50"
                >
                  Retry
                </button>
              </div>
            ) : rulesPopoverRules.length === 0 ? (
              <div className="px-4 py-5 text-sm font-medium text-muted-foreground">
                No rules available.
              </div>
            ) : (
              <ul className="min-h-0 flex-1 overflow-y-auto px-4 py-2 text-sm text-popover-foreground">
                {rulesPopoverRules.map((rule, index) => {
                  const kpiDisplay = getRuleKpiDisplay(rule);
                  const kpiLabel = getRuleKpiLabel(rule);
                  const allocStep = displayRuleValue(rule.allocStep);
                  const ruleText = displayRuleValue(rule.rule);

                  return (
                    <li
                      key={`${rule.ruleId}-${index}`}
                      className="border-b border-border py-2.5 last:border-b-0"
                    >
                      <span
                        className="block truncate text-sm font-semibold text-popover-foreground"
                        title={`${kpiLabel}: ${kpiDisplay} (${allocStep})`}
                      >
                        {kpiDisplay}
                        <span className="font-medium text-muted-foreground"> ({allocStep})</span>
                      </span>
                      <span className="mt-1 block truncate text-xs text-muted-foreground" title={ruleText}>
                        Rule: {ruleText}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      <SidePanel
        open={isCreateRunOpen}
        onOpenChange={(open) => {
          if (!open) closeCreateRunModal();
        }}
        title="Create a Run"
        width="min(68rem, 100vw)"
        minWidth="min(34rem, 100vw)"
        maxWidth="92vw"
        resizable
        resizeMinWidth={520}
        resizeMaxWidth={1320}
        resizeHandleAriaLabel="Resize create run panel"
        closeButtonAriaLabel="Close create run modal"
        headerClassName="border-b-0"
        contentClassName="px-6 pb-5"
        footerClassName="flex items-center justify-end gap-3"
        footer={(
          <>
            <button
              type="button"
              onClick={closeCreateRunModal}
              className="inline-flex h-[38px] items-center justify-center rounded-md border border-border bg-card px-5 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-muted"
            >
              Cancel
            </button>
            {canShowCreateRunForm && (
              <button
                type="button"
                onClick={handleCreateRun}
                disabled={isCreateRunDisabled}
                className="inline-flex h-[38px] items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
              >
                {isCreatingRun ? 'Creating...' : 'Create'}
              </button>
            )}
          </>
        )}
      >
        {isCheckingSapConnection ? (
          <div className="flex min-h-[18rem] flex-col items-center justify-center gap-3 rounded-lg border border-neutral-200 bg-white px-6 py-10 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
            <div>
              <p className="text-sm font-semibold text-neutral-900">Checking SAP Datasphere connection</p>
              <p className="mt-1 text-xs text-neutral-600">
                Please wait while we verify the data source before creating a run.
              </p>
            </div>
          </div>
        ) : sapConnectionError ? (
          <div className="flex min-h-[18rem] flex-col items-center justify-center gap-4 rounded-lg border border-error-200 bg-white px-6 py-10 text-center">
            <div>
              <p className="text-sm font-semibold text-error-700">Unable to check SAP Datasphere connection</p>
              <p className="mt-1 text-xs text-neutral-600">{sapConnectionError}</p>
            </div>
            <button
              type="button"
              onClick={() => void loadSapConnectionStatus()}
              className="inline-flex h-8 items-center justify-center rounded-md border border-error-200 bg-white px-3 text-xs font-semibold text-error-700 shadow-sm hover:bg-error-50"
            >
              Retry
            </button>
          </div>
        ) : !canShowCreateRunForm ? (
          <div className="flex min-h-[18rem] flex-col items-center justify-center gap-4 rounded-lg border border-neutral-200 bg-white px-6 py-10 text-center">
            <div>
              <p className="text-sm font-semibold text-neutral-900">SAP Datasphere connection required</p>
              <p className="mt-1 max-w-sm text-xs text-neutral-600">
                Connect to the data source before creating an allocation run.
              </p>
            </div>
            <button
              type="button"
              onClick={handleConnectDataSource}
              disabled={isConnectingSap}
              className="inline-flex h-[38px] items-center justify-center rounded-md bg-primary-500 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-500 disabled:shadow-none"
            >
              {isConnectingSap ? 'Connecting...' : 'Connect to data source'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_14rem]">
              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label htmlFor="allocation-run-title" className="text-sm font-semibold text-foreground">
                    Name <span className="text-error-700">*</span>
                  </label>
                  <span className="text-xs font-medium text-muted-foreground">{runTitle.length}/250</span>
                </div>
                <input
                  id="allocation-run-title"
                  value={runTitle}
                  onChange={(event) => setRunTitle(event.target.value)}
                  maxLength={250}
                  placeholder="Provide a name for the run."
                  className="h-[42px] w-full rounded-md border border-input bg-card px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                />
                <p className="mt-1 min-h-[1rem] text-xs text-error-700">
                  {hasAttemptedCreate && !runTitle.trim() ? 'Name is required.' : ''}
                </p>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label htmlFor="allocation-run-period" className="text-sm font-semibold text-foreground">
                    Period <span className="text-error-700">*</span>
                  </label>
                  <span className="text-xs font-medium text-muted-foreground">{runPeriod.length}/10</span>
                </div>
                <input
                  id="allocation-run-period"
                  value={runPeriod}
                  onChange={(event) => setRunPeriod(event.target.value)}
                  maxLength={10}
                  placeholder="Example: 2026010"
                  className="h-[42px] w-full rounded-md border border-input bg-card px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                />
                <p className="mt-1 min-h-[1rem] text-xs text-error-700">
                  {hasAttemptedCreate && !runPeriod.trim() ? 'Period is required.' : ''}
                </p>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-4">
                <label htmlFor="allocation-run-description" className="text-sm font-semibold text-foreground">
                  Description
                </label>
                <span className="text-xs font-medium text-muted-foreground">{runDescription.length}/500</span>
              </div>
              <textarea
                id="allocation-run-description"
                value={runDescription}
                onChange={(event) => setRunDescription(event.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Provide additional context or details about this run."
                className="w-full resize-none rounded-md border border-input bg-card px-3 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
              />
              <p className="mt-1 min-h-[1rem] text-xs text-error-700" />
            </div>

            <div>
              <div className="mb-3">
                <label className="text-sm font-semibold text-foreground">
                  Rules <span className="text-error-700">*</span>
                </label>
                <p className="text-xs text-muted-foreground">
                  Select one or more rules to include in this run.
                </p>
              </div>

              <MultiSelectDropdown<AllocationRule>
                placeholder="Select rules"
                options={allocationRules}
                selectedValues={selectedRuleIds}
                onChange={handleSelectedRulesChange}
                searchable
                searchPlaceholder="Search rules"
                showSelectAll
                selectAllLabel="Select all visible rules"
                selectAllDescription={`${allocationRules.length} of ${rulesTotal} rendered`}
                showSelectedCount
                selectedDisplayMode="count"
                showCheckboxes
                lazyLoad
                pageSize={RULES_PAGE_SIZE}
                hasMore={rulesHasMore}
                isLoading={isLoadingRules}
                onLoadMore={loadMoreAllocationRules}
                onSearch={searchAllocationRules}
                getOptionValue={(rule) => rule.id}
                getOptionLabel={formatAllocationRuleOption}
                renderOption={renderAllocationRuleOption}
                tableEnabled
                tableColumns={allocationRuleSelectionColumns}
                tableData={selectedRules}
                onDeleteSelectedItem={() => undefined}
                error={rulesError || (hasAttemptedCreate && selectedRuleIds.length === 0 ? 'Select at least one rule.' : '')}
                required
                minSelected={1}
                disabled={isCreatingRun}
                tableClassName="max-h-[24rem] overflow-auto"
              />
              {rulesError && (
                <button
                  type="button"
                  onClick={() => void loadAllocationRules({ search: rulesSearchTerm, offset: 0, append: false })}
                  className="mt-2 inline-flex h-8 items-center justify-center rounded-md border border-error-200 bg-card px-3 text-xs font-semibold text-error-700 hover:bg-error-50"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}
      </SidePanel>

      {selectedRun && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/45 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="run-details-title"
            className="flex max-h-[calc(100vh-2rem)] w-full max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl bg-card text-card-foreground shadow-[0_24px_70px_rgba(15,23,42,0.28)] xl:max-w-[96rem]"
          >
            <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5">
              <div className="flex min-w-0 items-center gap-3">
                <h2 id="run-details-title" className="truncate text-xl font-bold leading-6 tracking-normal text-card-foreground">
                  {runDetails?.name || selectedRun.name}
                </h2>
                <span className={`inline-flex min-w-[76px] flex-shrink-0 justify-center rounded-md px-2.5 py-1 text-xs font-semibold ${statusStyles[selectedRun.status]}`}>
                  {selectedRun.status}
                </span>
              </div>
              <button
                type="button"
                aria-label="Close run details modal"
                onClick={closeRunDetails}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
                <div className="min-w-0 max-w-[42rem] md:flex-shrink">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</p>
                  <p className="mt-1 text-sm leading-5 text-card-foreground">
                    {runDetails?.description || selectedRun.description || '-'}
                  </p>
                </div>
                <div className="min-w-[7rem] flex-shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Period</p>
                  <p className="mt-1 text-sm font-medium text-card-foreground">
                    {runDetails?.period || '-'}
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <h3 className="text-sm font-semibold text-card-foreground">Selected Rules</h3>
                  {isLoadingRunDetails && (
                    <span className="text-xs font-medium text-muted-foreground">Loading rules...</span>
                  )}
                </div>
                {runDetails?.rules.length ? (
                  <div className="mb-3 flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3 lg:flex-row lg:items-center">
                    <label className="relative block h-9 min-w-0 flex-1">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        value={ruleResultsSearchTerm}
                        onChange={(event) => setRuleResultsSearchTerm(event.target.value)}
                        placeholder="Filter by rule, source, KPI, counts, or result"
                        className="h-9 w-full rounded-md border border-border bg-card pl-10 pr-9 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                      />
                      {ruleResultsSearchTerm && (
                        <button
                          type="button"
                          onClick={() => setRuleResultsSearchTerm('')}
                          className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          title="Clear rule results search"
                          aria-label="Clear rule results search"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </label>
                    <select
                      value={ruleResultsStatusFilter}
                      onChange={(event) => setRuleResultsStatusFilter(event.target.value as RuleResultStatusFilter)}
                      aria-label="Filter rule results by run status"
                      className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm outline-none transition-colors focus:border-primary lg:w-40"
                    >
                      <option value="all">All Statuses</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                      <option value="in_progress">In Progress</option>
                    </select>
                    <select
                      value={ruleResultsResultFilter}
                      onChange={(event) => setRuleResultsResultFilter(event.target.value as RuleValidationResultFilter)}
                      aria-label="Filter rule results by validation result"
                      className="h-9 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm outline-none transition-colors focus:border-primary lg:w-40"
                    >
                      <option value="all">All Results</option>
                      <option value="all_good">All Good</option>
                      <option value="needs_review">Needs Review</option>
                      <option value="none">No Result</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setRuleResultsSearchTerm('');
                        setRuleResultsStatusFilter('all');
                        setRuleResultsResultFilter('all');
                      }}
                      disabled={!hasRuleResultFilters}
                      className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-card px-3 text-xs font-semibold text-foreground shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground/50 disabled:hover:bg-card"
                    >
                      Clear
                    </button>
                  </div>
                ) : null}

                {runDetailsError ? (
                  <div className="rounded-md border border-error-100 bg-error-50 px-3 py-3 text-sm text-error-700">
                    {runDetailsError}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border bg-card">
                    {!runDetails?.rules.length && !isLoadingRunDetails ? (
                      <div className="px-3 py-5 text-center text-sm font-medium text-muted-foreground">
                        No rules available.
                      </div>
                    ) : !filteredRunDetailRules.length && !isLoadingRunDetails ? (
                      <div className="px-3 py-5 text-center text-sm font-medium text-muted-foreground">
                        No rules match the selected filters.
                      </div>
                    ) : (
                      <div className="max-h-[22rem] overflow-auto">
                        <table className="w-full min-w-[116rem] table-auto border-collapse text-left">
                          <thead className="sticky top-0 z-10 bg-muted text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                            <tr className="border-b border-border">
                              {runDetailRuleColumns.map((column) => (
                                <th
                                  key={column.apiKey}
                                  className={`px-3 py-2.5 align-middle ${getColumnAlignClass(column.align)} ${getRunDetailRuleHeaderClass(column.apiKey)} ${column.className || ''}`}
                                >
                                  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${getColumnHeaderAlignClass(column.align)}`}>
                                    <span title={column.header}>
                                      {column.header}
                                    </span>
                                    <ActionTooltip content={column.tooltip}>
                                      <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground">
                                        <Info className="h-3 w-3" />
                                      </span>
                                    </ActionTooltip>
                                  </span>
                                </th>
                              ))}
                              <th className="sticky right-0 z-30 w-[4.5rem] bg-muted px-2.5 py-2 text-center shadow-[-1px_0_0_hsl(var(--border))]">
                                Download
                              </th>
                            </tr>
                          </thead>
                          <tbody className="text-xs text-card-foreground">
                            {filteredRunDetailRules.map((rule) => {
                              const isFailed = isFailedRuleStatus(rule.status);
                              const failedCount = parseCount(rule.failed);
                              const validationResult = getRuleValidationResult(rule);

                              return (
                                <React.Fragment key={rule.ruleId}>
                                  <tr className="group border-b border-border/70 hover:bg-accent/60">
                                    {runDetailRuleColumns.map((column) => (
                                      <td
                                        key={`${rule.ruleId}-${column.apiKey}`}
                                        className={`px-3 py-2.5 align-top ${getColumnAlignClass(column.align)} ${getRunDetailRuleCellClass(column.apiKey)} ${column.className || ''}`}
                                      >
                                        {column.apiKey === 'status' ? (
                                          <div className="flex flex-col items-center gap-1">
                                            <span className={`inline-flex min-w-[68px] justify-center rounded-md px-2 py-1 text-[0.7rem] font-semibold ${getRuleStatusClass(rule.status)}`}>
                                              {formatRuleStatus(rule.status)}
                                            </span>
                                            {isFailed && rule.errorMessage && (
                                              <span className="max-w-[12rem] break-words text-center text-[0.65rem] leading-tight text-error-600" title={rule.errorMessage}>
                                                {rule.errorMessage}
                                              </span>
                                            )}
                                          </div>
                                        ) : column.apiKey === 'validation_result' ? (
                                          validationResult ? (
                                            <span className={`inline-flex min-w-[86px] justify-center rounded-md px-2 py-1 text-[0.7rem] font-semibold ${getRuleValidationResultClass(validationResult)}`}>
                                              {validationResult}
                                            </span>
                                          ) : (
                                            <span className="block whitespace-nowrap" title="-">
                                              -
                                            </span>
                                          )
                                        ) : column.apiKey === 'failed' ? (
                                          <span
                                            className={`block whitespace-nowrap font-semibold ${failedCount > 0 ? 'text-error-700' : 'text-success-700'}`}
                                            title={displayRuleValue(column.value(rule))}
                                          >
                                            {displayRuleValue(column.value(rule))}
                                          </span>
                                        ) : (
                                          <span className="block whitespace-nowrap" title={displayRuleValue(column.value(rule))}>
                                            {displayRuleValue(column.value(rule))}
                                          </span>
                                        )}
                                      </td>
                                    ))}
                                    <td className="sticky right-0 z-20 bg-card px-2.5 py-2 text-center align-top shadow-[-1px_0_0_hsl(var(--border))] group-hover:bg-accent/60">
                                      <ActionTooltip content={rule.blobUrl ? 'Download' : 'Download unavailable'}>
                                        <button
                                          type="button"
                                          aria-label={`Download ${rule.rule} results`}
                                          disabled={!rule.blobUrl || downloadingRuleId === rule.ruleId}
                                          onClick={() => void handleDownloadRuleResult(rule)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-card-foreground transition-colors hover:bg-accent hover:text-primary disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:bg-transparent"
                                        >
                                          <Download className={`h-4 w-4 ${downloadingRuleId === rule.ruleId ? 'animate-pulse' : ''}`} />
                                        </button>
                                      </ActionTooltip>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};
